import React from 'react';
import { Button, ButtonLink, Flex, Heading, SrOnly, Table, Text } from '@applyboard/crystal-ui';
import { DeleteOutlineIcon, DocumentAlertHifiIcon, EditOutlineIcon } from '@applyboard/ui-icons';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useSearchResult } from '../context';
import type { ConversionTableResource } from '@ab/ams-school-api/types/private/v1';
import { Flag } from '~/components/Flag';
import { DeleteConversionTableDialog } from './DeleteConversionTableDialog';

export function GradeConversionTable() {
  const { apiError } = useErrorHandler();
  const { conversionTables, isFetchingConversionTables, conversionTablesError } = useSearchResult();
  const [deletingConversionTable, setDeletingConversionTable] = React.useState<ConversionTableResource | null>(null);

  if (conversionTablesError) {
    apiError();
    return null;
  }

  return (
    <Flex direction="column">
      <div role="alert" aria-live="assertive">
        {isFetchingConversionTables && <SrOnly>Loading conversion tables</SrOnly>}
      </div>
      <Table
        data={conversionTables}
        loading={isFetchingConversionTables}
        overflow="scroll"
        columns={[
          {
            accessorKey: 'attributes.name',
            header: 'Name',
            cell: (conversionTable: ConversionTableResource) => (
              <Table.TextCell>{conversionTable.attributes.name}</Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.sourceCountry',
            header: 'Country',
            cell: (conversionTable: ConversionTableResource) => (
              <Table.Cell>
                {conversionTable.attributes.sourceCountryCode ? (
                  <Flex align="center" gap={2} id={`${conversionTable.attributes.sourceCountryCode}-country-code`}>
                    <Flag nationalityCode={conversionTable.attributes.sourceCountryCode} />
                    <Text>{conversionTable.attributes.sourceCountryCode}</Text>
                  </Flex>
                ) : (
                  <Text>-</Text>
                )}
              </Table.Cell>
            ),
          },
          {
            accessorKey: 'attributes.sourceInstitutionName',
            header: 'Source Institution Name',
            cell: (conversionTable: ConversionTableResource) => (
              <Table.TextCell>{conversionTable.attributes.sourceInstitutionName}</Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.included.grade_schema.attributes.gradeSchemaType',
            header: 'Source Grade Type',
            cell: (conversionTable: ConversionTableResource) => (
              <Table.TextCell>
                {conversionTable.included?.find((included) => included.type === 'grade_schema')?.attributes
                  ?.gradeSchemaType ?? '-'}
              </Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.destinationGradeType',
            header: 'Destination Grade Type',
            cell: (conversionTable: ConversionTableResource) => (
              <Table.TextCell>{conversionTable.attributes.destinationGradeType}</Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.sourceEducationLevelName',
            header: 'Source Education Level',
            cell: (conversionTable: ConversionTableResource) => (
              <Table.TextCell>{conversionTable.attributes.educationLevelName ?? '-'}</Table.TextCell>
            ),
          },
          {
            accessorKey: 'attributes.updatedAt',
            header: 'Last modified',
            cell: (conversionTable: ConversionTableResource) => (
              <Table.TextCell>{new Date(conversionTable.attributes.updatedAt).toLocaleDateString()}</Table.TextCell>
            ),
          },
          {
            accessorKey: 'link',
            header: () => <SrOnly>Conversion Table Link</SrOnly>,
            cell: (conversionTable: ConversionTableResource) => {
              return (
                <Flex>
                  <ButtonLink
                    aria-label={`${conversionTable.attributes.name} Link`}
                    emphasis="transparent"
                    href={`/conversion-rules/grade-conversions/${conversionTable.id}`}
                    intent="secondary"
                    leadIcon={EditOutlineIcon}
                    size="sm"
                    width="hug"
                  />
                  <Button
                    aria-label={`Delete ${conversionTable.attributes.name}`}
                    emphasis="transparent"
                    intent="negative"
                    leadIcon={DeleteOutlineIcon}
                    size="sm"
                    width="hug"
                    onClick={() => setDeletingConversionTable(conversionTable)}
                  />
                </Flex>
              );
            },
          },
        ]}
      >
        <Table.NoData>
          <Flex align="center" justify="center" direction="column" gap={4} py={32}>
            <DocumentAlertHifiIcon />
            <Heading level={3}>No conversion tables found</Heading>
            <Text contrast="mid">Try adjusting your filters to see results.</Text>
          </Flex>
        </Table.NoData>
      </Table>
      {deletingConversionTable && (
        <DeleteConversionTableDialog
          conversionTable={deletingConversionTable}
          onClose={() => setDeletingConversionTable(null)}
        />
      )}
    </Flex>
  );
}
